import { fetchPmsSyncStatus } from 'features/Welcome/HotelSwitcher/pmsSyncStatusSlice'
import { useEffect, useRef, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'

import { selectIsInternalAdmin } from '../../../accessors/index'
import { getMatchScore } from '../../../utils/listSearch'
import { HotelSelectorGroup } from './HotelSelectorGroup'
import { getSearchableString } from './hotelSelectorHelpers'
import AssignedIcon from './my-assigned.svg'

export const HotelSelector = ({ children, handleSelect }) => {
  const [searchParam, setSearchParam] = useState('')

  const inputRef = useRef()
  const isMountedRef = useRef()

  useEffect(() => {
    isMountedRef.current = true

    return () => {
      isMountedRef.current = false
    }
  }, [])

  const dispatch = useDispatch()

  useEffect(() => {
    if (inputRef.current) {
      setTimeout(() => {
        inputRef?.current?.focus()
      }, 0)
    }
  }, [])

  const handleFilterChange = e => setSearchParam(e.target.value)

  const subsidiaryList = useSelector(s => s.login.subsidiaryList)

  const isInternalAdmin = useSelector(selectIsInternalAdmin)

  const isCsmUser = useSelector(s => s.login.isCsmUser)

  const isDemoMode = useSelector(s => s.login.isDemoMode)

  useEffect(() => {
    if (isInternalAdmin) {
      dispatch(fetchPmsSyncStatus())
    }
  }, [dispatch, isInternalAdmin])

  const myAssignedSubsidiaries = subsidiaryList.filter(s =>
    isCsmUser ? s.isCurrentUserCsm : true
  )
  const otherSubsidiaries = isCsmUser
    ? subsidiaryList.filter(s => !s.isCurrentUserCsm)
    : subsidiaryList

  const groupedMyAssignedSubsidiaries = myAssignedSubsidiaries.reduce((prev, curr) => {
    if (prev[curr.merchantName]) {
      return {
        ...prev,
        [curr.merchantName]: [...prev[curr.merchantName], curr]
      }
    } else {
      return { ...prev, [curr.merchantName]: [curr] }
    }
  }, {})

  const formattedMyAssignedSubsidiaries = Object.keys(
    groupedMyAssignedSubsidiaries
  ).reduce((prev, curr, index) => {
    const groupName = isDemoMode ? `Hotel Group ${index + 1}` : curr
    return { ...prev, [groupName]: groupedMyAssignedSubsidiaries[curr] }
  }, {})

  const groupedOtherSubsidiaries = otherSubsidiaries.reduce((prev, curr) => {
    if (prev[curr.merchantName]) {
      return {
        ...prev,
        [curr.merchantName]: [...prev[curr.merchantName], curr]
      }
    } else {
      return { ...prev, [curr.merchantName]: [curr] }
    }
  }, {})

  const formattedOtherSubsidiaries = Object.keys(groupedOtherSubsidiaries).reduce(
    (prev, curr, index) => {
      const groupName = isDemoMode ? `Hotel Group ${index + 1}` : curr
      return { ...prev, [groupName]: groupedOtherSubsidiaries[curr] }
    },
    {}
  )

  const isSearchMode =
    searchParam.trim() !== '' && !(searchParam.length === 36 && searchParam.includes('-'))

  function sortGroupNamesByRelevance(a, b, search, formattedSubsidiaries, isDemo) {
    if (!isSearchMode || !formattedSubsidiaries) {
      if (isDemo) {
        const indexA = parseInt(a.match(/\d+/)[0], 10)
        const indexB = parseInt(b.match(/\d+/)[0], 10)
        return indexA - indexB
      }
      return a.localeCompare(b)
    }
    const hotelsA = formattedSubsidiaries[a] || []
    const hotelsB = formattedSubsidiaries[b] || []
    const maxScoreA = Math.max(
      0,
      ...hotelsA.map(h => getMatchScore(search, getSearchableString(h, a)))
    )
    const maxScoreB = Math.max(
      0,
      ...hotelsB.map(h => getMatchScore(search, getSearchableString(h, b)))
    )
    if (maxScoreB !== maxScoreA) return maxScoreB - maxScoreA
    if (isDemo) {
      const indexA = parseInt(a.match(/\d+/)[0], 10)
      const indexB = parseInt(b.match(/\d+/)[0], 10)
      return indexA - indexB
    }
    return a.localeCompare(b)
  }

  if (!subsidiaryList) {
    return 'loading'
  }

  const handleBlur = () => {
    if (isMountedRef?.current) {
      inputRef?.current.focus()
    }
  }

  return (
    <div className="hotel-selector">
      <div className="hotel-selector__filter" style={{ display: 'flex' }}>
        <input
          type="search"
          placeholder="Search..."
          onChange={handleFilterChange}
          value={searchParam}
          ref={inputRef}
          onBlur={handleBlur}
        />
      </div>
      <ul>
        {isCsmUser && (
          <>
            <div className="hotel-selector__my-assigned">
              <img src={AssignedIcon} alt="Assigned" />
              Hotels you're assigned to
            </div>
            <HotelSelectorGroup
              groupName="Independently Managed Hotels"
              isInternalAdmin={isInternalAdmin}
              formattedSubsidiaries={formattedMyAssignedSubsidiaries}
              searchParam={searchParam}
              handleSelect={handleSelect}
              key="Independently Managed Hotels"
            />
            {Object.keys(formattedMyAssignedSubsidiaries)
              .filter(k => k !== 'Independently Managed Hotels')
              .sort((a, b) => sortGroupNamesByRelevance(a, b, searchParam, formattedMyAssignedSubsidiaries, isDemoMode))
              .map(groupName => (
                <HotelSelectorGroup
                  groupName={groupName}
                  isInternalAdmin={isInternalAdmin}
                  formattedSubsidiaries={formattedMyAssignedSubsidiaries}
                  searchParam={searchParam}
                  handleSelect={handleSelect}
                  key={groupName}
                />
              ))}
            <div className="hotel-selector__others">Other Hotels</div>
          </>
        )}
        <HotelSelectorGroup
          groupName="Independently Managed Hotels"
          isInternalAdmin={isInternalAdmin}
          formattedSubsidiaries={formattedOtherSubsidiaries}
          searchParam={searchParam}
          handleSelect={handleSelect}
          key="Independently Managed Hotels"
        />
        {Object.keys(formattedOtherSubsidiaries)
          .filter(k => k !== 'Independently Managed Hotels')
          .sort((a, b) => sortGroupNamesByRelevance(a, b, searchParam, formattedOtherSubsidiaries, isDemoMode))
          .map(groupName => (
            <HotelSelectorGroup
              groupName={groupName}
              isInternalAdmin={isInternalAdmin}
              formattedSubsidiaries={formattedOtherSubsidiaries}
              searchParam={searchParam}
              handleSelect={handleSelect}
              key={groupName}
            />
          ))}
        {children}
      </ul>
    </div>
  )
}
